import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StudentNav } from '../../components/shared/BottomNav';
import {
  subscribeToCurrentWeek,
  subscribeToWeekTransactions,
  addTransaction,
  recalculateWeekTotals,
  convertPlannedToActual,
  deleteTransaction,
  updateTransaction,
  saveRecurringTemplate,
  deleteRecurringTemplate,
  subscribeToRecurringTemplates,
  getWeekRecurringApplied,
  markWeekRecurringApplied,
  getUnclosedPreviousWeek,
  detectBacklog,
  clearBacklog,
  getOrCreateCurrentWeek,
  getOrCreateNextWeek,
  getCurrentWeekId,
  getNextWeekId,
  formatCurrency,
  calculateMatch,
} from '../../services/firebase';

const CATEGORIES = [
  { id: 'food', label: 'Food', emoji: '🍕' },
  { id: 'groceries', label: 'Groceries', emoji: '🛒' },
  { id: 'transport', label: 'Transport', emoji: '🚗' },
  { id: 'entertainment', label: 'Fun', emoji: '🎬' },
  { id: 'clothing', label: 'Clothing', emoji: '👕' },
  { id: 'health', label: 'Health', emoji: '💊' },
  { id: 'books', label: 'Books', emoji: '📚' },
  { id: 'other', label: 'Other', emoji: '📦' },
];

const INCOME_CATEGORIES = [
  { id: 'gift', label: 'Gift', emoji: '🎁' },
  { id: 'job', label: 'Job', emoji: '💼' },
  { id: 'refund', label: 'Refund', emoji: '↩️' },
  { id: 'other', label: 'Other', emoji: '💵' },
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Returns today's day index (0=Sun … 6=Sat)
const todayIndex = () => new Date().getDay();

// Returns a label like "Today", "Yesterday", or the day name
// asHeader=true returns "As of Today" for actual spending section headers
const dayLabel = (dayIndex, type, asHeader = false) => {
  const today = todayIndex();
  if (dayIndex === today) {
    return (type === 'actual' && asHeader) ? 'As of Today' : 'Today';
  }
  if (dayIndex === (today + 6) % 7) return 'Yesterday';
  return DAYS[dayIndex];
};

const StudentDashboard = () => {
  const { family, userDoc, authUser, refreshKey } = useAuth();
  const navigate = useNavigate();
  const [currentWeek, setCurrentWeek] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [recurringTemplates, setRecurringTemplates] = useState([]);
  const [showRecurringPrompt, setShowRecurringPrompt] = useState(false);
  const [recurringPromptDismissed, setRecurringPromptDismissed] = useState(false);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [reviewPromptDismissed, setReviewPromptDismissed] = useState(false);
  const [defaultType, setDefaultType] = useState('actual');
  const [convertTx, setConvertTx] = useState(null);
  const [editTx, setEditTx] = useState(null);
  const [unclosedPrevWeek, setUnclosedPrevWeek] = useState(null);
  const [backlog, setBacklog] = useState(null);         // array of unclosed weeks if 2+
  const [backlogClearing, setBacklogClearing] = useState(false);
  const [backlogDismissed, setBacklogDismissed] = useState(false);
  const [nextWeekTransactions, setNextWeekTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const studentFirst = userDoc?.displayName?.split(' ')[0] || 'Hey';
  const allowance = family?.allowance?.amount || 0;
  const matchConfig = family?.match;

  useEffect(() => {
    if (!family?.id) return;
    setLoading(true);
    setCurrentWeek(null);
    setTransactions([]);
    getOrCreateCurrentWeek(family.id, allowance, family).catch(console.error);
    const unsubWeek = subscribeToCurrentWeek(family.id, (week) => {
      setCurrentWeek(week);
      setLoading(false);
    });
    return unsubWeek;
  }, [family?.id, allowance, refreshKey]);

  // Check for unclosed previous weeks — re-check on resume too
  useEffect(() => {
    if (!family?.id) return;
    getUnclosedPreviousWeek(family.id)
      .then(week => setUnclosedPrevWeek(week))
      .catch(console.error);
  }, [family?.id, refreshKey]);

  // Detect 2+ unclosed prior weeks — prompt "start fresh" if found
  useEffect(() => {
    if (!family?.id || backlogDismissed) return;
    detectBacklog(family.id).then(unclosed => {
      if (unclosed.length >= 2) setBacklog(unclosed);
    }).catch(console.error);
  }, [family?.id, refreshKey, backlogDismissed]);

  useEffect(() => {
    if (!family?.id) return;
    const weekId = getCurrentWeekId();
    const unsub = subscribeToWeekTransactions(family.id, weekId, (txs) => {
      setTransactions(txs);
    });
    return unsub;
  }, [family?.id, refreshKey]);

  useEffect(() => {
    if (!family?.id) return;
    const weekId = getNextWeekId();
    const unsub = subscribeToWeekTransactions(family.id, weekId, (txs) => {
      setNextWeekTransactions(txs.filter(t => t.type === 'planned'));
    });
    return unsub;
  }, [family?.id, refreshKey]);

  // Subscribe to recurring templates
  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToRecurringTemplates(family.id, setRecurringTemplates);
    return unsub;
  }, [family?.id]);

  // Show recurring prompt when new week starts and templates exist but haven't been applied yet
  useEffect(() => {
    if (!family?.id || !currentWeek?.id || recurringPromptDismissed) return;
    if (recurringTemplates.length === 0) return;
    getWeekRecurringApplied(family.id, currentWeek.id).then(applied => {
      if (!applied) setShowRecurringPrompt(true);
    }).catch(console.error);
  }, [family?.id, currentWeek?.id, recurringTemplates.length, recurringPromptDismissed]);

  const handleAddTransaction = async ({ amount, category, note, type, spendDate, weekOffset, repeatWeekly }) => {
    if (!family?.id || !currentWeek) return;
    const isNextWeek = type === 'planned' && weekOffset === 1;
    const weekId = isNextWeek ? getNextWeekId() : getCurrentWeekId();
    if (isNextWeek) await getOrCreateNextWeek(family.id, allowance);
    await addTransaction(family.id, {
      weekId, amount: parseFloat(amount), category, note, type,
      spendDate: spendDate !== undefined ? spendDate : todayIndex(),
      createdBy: authUser.uid,
    });
    // Save as recurring template if toggled
    if (repeatWeekly && type === 'planned') {
      await saveRecurringTemplate(family.id, {
        amount: parseFloat(amount), category, note,
        spendDay: spendDate !== undefined ? spendDate : todayIndex(),
      });
    }
    // Only recalculate totals for current week (next week doc manages itself)
    if (!isNextWeek) {
      const updatedTxs = [
        ...transactions,
        { amount: parseFloat(amount), type, category, note, spendDate },
      ];
      await recalculateWeekTotals(family.id, weekId, updatedTxs);
    }
    setShowAddSheet(false);
  };

  const handleApplyRecurring = async (selectedIds) => {
    if (!family?.id || !currentWeek) return;
    const weekId = getCurrentWeekId();
    const templatesToApply = recurringTemplates.filter(t => selectedIds.includes(t.id));
    const newTxs = [];
    for (const t of templatesToApply) {
      await addTransaction(family.id, {
        weekId, amount: t.amount, category: t.category,
        note: t.note, type: 'planned', spendDate: t.spendDay,
        createdBy: authUser.uid,
      });
      newTxs.push({ amount: t.amount, type: 'planned', category: t.category,
        note: t.note, spendDate: t.spendDay });
    }
    if (newTxs.length > 0) {
      await recalculateWeekTotals(family.id, weekId, [...transactions, ...newTxs]);
    }
    await markWeekRecurringApplied(family.id, weekId);
    setShowRecurringPrompt(false);
  };

  const handleConvert = async (actualAmount, spendDate) => {
    if (!convertTx || !family?.id) return;
    const weekId = getCurrentWeekId();
    await convertPlannedToActual(family.id, weekId, convertTx, actualAmount, transactions, spendDate);
    setConvertTx(null);
  };

  const handleDelete = async (tx) => {
    if (!family?.id) return;
    await deleteTransaction(family.id, tx.id, transactions);
  };

  const handleDeleteNextWeek = async (tx) => {
    if (!family?.id) return;
    const weekId = getNextWeekId();
    await deleteTransaction(family.id, tx.id, nextWeekTransactions, weekId);
  };

  const handleEdit = async (tx, updates) => {
    if (!family?.id) return;
    await updateTransaction(family.id, tx.id, updates, transactions);
    setEditTx(null);
  };

  const handleClearBacklog = async () => {
    if (!backlog || !family?.id) return;
    setBacklogClearing(true);
    try {
      await clearBacklog(family.id, allowance, backlog, family.parentId);
      setBacklog(null);
      setUnclosedPrevWeek(null);
      // Re-init the current week so carryover picks up correctly
      await getOrCreateCurrentWeek(family.id, allowance, family);
    } catch (err) {
      console.error('Failed to clear backlog:', err);
    } finally {
      setBacklogClearing(false);
    }
  };

  // Derived display values
  const spent = currentWeek?.spent || 0;
  const planned = currentWeek?.plannedSpend || 0;
  const bonusIncome = currentWeek?.bonusIncome || 0;
  const saved = currentWeek?.saved || 0;
  const carryover = currentWeek?.carryoverFromPrev || 0;
  const matchEarned = currentWeek?.matchEarned || calculateMatch(saved, matchConfig);
  const totalAvailable = (currentWeek?.allowanceAmount || allowance) + bonusIncome;
  const projectedSpend = spent + planned;
  const remaining = Math.max(0, totalAvailable - spent - planned - saved);
  const spentPct = totalAvailable > 0 ? Math.min(100, (spent / totalAvailable) * 100) : 0;
  const plannedPct = totalAvailable > 0 ? Math.min(100, (planned / totalAvailable) * 100) : 0;
  const savedPct = totalAvailable > 0 ? Math.min(100, (saved / totalAvailable) * 100) : 0;
  const remainPct = Math.max(0, 100 - spentPct - plannedPct - savedPct);
  const isClosed = currentWeek?.closedOut;
  const isOverBudget = projectedSpend + saved > totalAvailable;
  const nearBudget = !isOverBudget && totalAvailable > 0 && (projectedSpend + saved) / totalAvailable > 0.8;
  const nearLimit = remaining <= totalAvailable * 0.15 && spent > 0;
  const now = new Date();
  const isSunday = now.getDay() === 0 && now.getHours() >= 18; // last day of Mon-Sun week, after 6pm
  const tomorrowIndex = (todayIndex() + 1) % 7;

  const actualTxs = transactions.filter(t => t.type === 'actual' || !t.type);
  const plannedTxs = transactions.filter(t => t.type === 'planned');

  // Show review prompt when overdue planned items exist and student hasn't dismissed this session
  // Placed here so isClosed and plannedTxs are already declared
  useEffect(() => {
    if (!currentWeek?.id || reviewPromptDismissed || isClosed) return;
    const todayDay = new Date().getDay();
    const overdue = plannedTxs.filter(t => {
      if (t.spendDate === undefined) return false;
      const itemPos = t.spendDate === 0 ? 7 : t.spendDate;
      const todayPos = todayDay === 0 ? 7 : todayDay;
      return itemPos < todayPos;
    });
    setShowReviewPrompt(overdue.length > 0);
  }, [currentWeek?.id, plannedTxs, reviewPromptDismissed, isClosed]);
  const incomeTxs = transactions.filter(t => t.type === 'income');

  // Waiting for Monday — show placeholder
  if (family?.startOnSunday && !family?.walletStarted) {
    const now = new Date();
    const daysUntilMonday = now.getDay() === 1 ? 7 : (8 - now.getDay()) % 7 || 7;
    const nextMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysUntilMonday);
    const daysLeft = Math.ceil((nextMonday - now) / 86400000);
    const dateStr = nextMonday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return (
      <div className="screen">
        <StudentNav />
        <div className="page-header">
          <div>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-3)', marginBottom: 2 }}>Welcome</p>
            <h1 className="page-title">{studentFirst}'s Wallet</h1>
          </div>
        </div>
        <div className="card--hero animate-in" style={{ textAlign: 'center', padding: '32px 20px', marginBottom: 16 }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <circle cx="18" cy="18" r="14" stroke="white" strokeWidth="2" strokeOpacity="0.6"/>
              <line x1="18" y1="10" x2="18" y2="18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <line x1="18" y1="18" x2="24" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>
            Your wallet opens Monday
          </div>
          <div style={{ fontSize: '0.875rem', opacity: 0.8, marginBottom: 10, lineHeight: 1.5 }}>
            Your {formatCurrency(allowance)} weekly allowance will be ready on {dateStr} by 9am ET.
          </div>
          <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>
            {daysLeft === 1 ? '1 day to go' : `${daysLeft} days to go`}
          </div>
        </div>
        <div className="card" style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>💡</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 4 }}>How it works</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)', lineHeight: 1.6 }}>
                Each week runs Monday to Sunday. Log what you spend, plan ahead, and save what's left to earn your match.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <StudentNav />

      <div className="page-header">
        <div>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-3)', marginBottom: 2 }}>
            Welcome back
          </p>
          <h1 className="page-title">{studentFirst}'s Wallet</h1>
        </div>
        {isClosed && <span className="badge badge--green">Week closed ✓</span>}
      </div>

      {/* Unclosed previous week banner */}
      {unclosedPrevWeek && (
        <div style={{
          background: '#FFF3E0',
          border: '1.5px solid #F4A261',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 16px',
          marginBottom: 20,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#C45E1A', marginBottom: 4 }}>
              Last week isn't closed out yet
            </div>
            <div style={{ fontSize: '0.8125rem', color: '#C45E1A', opacity: 0.85, marginBottom: 12, lineHeight: 1.5 }}>
              Close it out to lock in your savings, earn your match, and carry the right balance into this week.
            </div>
            <button
              className="btn"
              style={{ background: 'var(--color-accent)', color: 'white',
                fontSize: '0.8125rem', padding: '8px 16px' }}
              onClick={() => navigate('/close-out', { state: { weekId: unclosedPrevWeek.id, week: unclosedPrevWeek } })}
            >
              Close out last week →
            </button>
          </div>
        </div>
      )}

      {/* Hero card */}
      <div className="card--hero animate-in" style={{ marginBottom: 20 }}>

        {/* Big number — always the first thing you see */}
        <p style={{ opacity: 0.75, fontSize: '0.8125rem', marginBottom: 4 }}>
          Left to spend this week
        </p>
        <div className="amount-large" style={{ marginBottom: 16,
          color: isOverBudget ? '#FFDDD2' : 'white' }}>
          {formatCurrency(remaining)}
        </div>

        {/* Budget breakdown — only shown when carryover or bonus income is present */}
        {(carryover !== 0 || bonusIncome > 0) && (
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8,
            padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ fontSize: '0.6875rem', opacity: 0.6, fontWeight: 600,
              letterSpacing: '0.05em', marginBottom: 8 }}>
              THIS WEEK'S BUDGET
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              marginBottom: 5 }}>
              <span style={{ fontSize: '0.75rem', opacity: 0.75 }}>Allowance</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {formatCurrency(currentWeek?.allowanceAmount - carryover || allowance)}
              </span>
            </div>
            {carryover !== 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between',
                marginBottom: 5 }}>
                <span style={{ fontSize: '0.75rem', opacity: 0.75 }}>
                  {carryover > 0 ? 'Rolled forward' : 'Overspent last week'}
                </span>
                <span style={{ fontSize: '0.75rem', fontWeight: 600,
                  color: carryover < 0 ? '#FFDDD2' : 'rgba(255,255,255,0.9)' }}>
                  {carryover > 0 ? '+' : ''}{formatCurrency(carryover)}
                </span>
              </div>
            )}
            {bonusIncome > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between',
                marginBottom: 5 }}>
                <span style={{ fontSize: '0.75rem', opacity: 0.75 }}>Bonus income</span>
                <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  +{formatCurrency(bonusIncome)}
                </span>
              </div>
            )}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: 8, marginTop: 6,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Total budget</span>
              <span style={{ fontSize: '0.875rem', fontWeight: 700 }}>
                {formatCurrency(totalAvailable)}
              </span>
            </div>
          </div>
        )}

        {/* Spent / Planned / Projected tiles */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)',
            borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: '0.6875rem', opacity: 0.7, marginBottom: 3 }}>Spent</div>
            <div style={{ fontSize: '1.0625rem', fontWeight: 700 }}>{formatCurrency(spent)}</div>
          </div>
          {planned > 0 && (
            <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)',
              borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: '0.6875rem', opacity: 0.7, marginBottom: 3 }}>Planned</div>
              <div style={{ fontSize: '1.0625rem', fontWeight: 700 }}>{formatCurrency(planned)}</div>
            </div>
          )}
          {planned > 0 && (
            <div style={{ flex: 1, borderRadius: 8, padding: '10px 12px',
              background: isOverBudget
                ? 'rgba(220,50,50,0.3)'
                : nearBudget
                  ? 'rgba(244,162,97,0.3)'
                  : 'rgba(255,255,255,0.15)',
              border: isOverBudget
                ? '1px solid rgba(255,120,120,0.4)'
                : nearBudget
                  ? '1px solid rgba(244,162,97,0.4)'
                  : 'none',
            }}>
              <div style={{ fontSize: '0.6875rem', opacity: 0.7, marginBottom: 3 }}>Projected</div>
              <div style={{ fontSize: '1.0625rem', fontWeight: 700,
                color: isOverBudget ? '#FFDDD2' : nearBudget ? '#FFE8CC' : 'white' }}>
                {formatCurrency(projectedSpend)}
              </div>
            </div>
          )}
        </div>

        {/* Bar */}
        <div style={{ height: 10, background: 'rgba(255,255,255,0.2)',
          borderRadius: 'var(--radius-full)', overflow: 'hidden', display: 'flex', marginBottom: 10 }}>
          <div style={{ width: `${spentPct}%`, background: '#F4A261', transition: 'width 0.6s ease' }} />
          <div style={{ width: `${plannedPct}%`,
            background: 'repeating-linear-gradient(45deg,#F4A261,#F4A261 4px,#FFDDD2 4px,#FFDDD2 8px)',
            transition: 'width 0.6s ease' }} />
          <div style={{ width: `${savedPct}%`, background: '#52B788', transition: 'width 0.6s ease' }} />
          <div style={{ width: `${remainPct}%`, background: 'rgba(255,255,255,0.15)' }} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: '0.75rem', opacity: 0.85 }}>
          <LegendItem color="#F4A261" label="Spent" value={formatCurrency(spent)} />
          {planned > 0 && <LegendItem color="#FFDDD2" label="Planned" value={formatCurrency(planned)} striped />}
          {saved > 0 && <LegendItem color="#52B788" label="Saved" value={formatCurrency(saved)} />}
          <LegendItem color="rgba(255,255,255,0.3)" label="Left" value={formatCurrency(remaining)} />
        </div>

        {matchConfig?.enabled && saved > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12,
            borderTop: '1px solid rgba(255,255,255,0.2)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ opacity: 0.85, fontSize: '0.875rem' }}>🎯 Match earned</span>
            <span style={{ fontWeight: 700 }}>{formatCurrency(matchEarned)}</span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {!isClosed && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20 }}>
          <ActionButton emoji="💸" label="Log spend"
            onClick={() => { setDefaultType('actual'); setShowAddSheet(true); }} />
          <ActionButton emoji="📅" label="Plan spend"
            onClick={() => { setDefaultType('planned'); setShowAddSheet(true); }} />
          <ActionButton emoji="🎁" label="Add income"
            onClick={() => { setDefaultType('income'); setShowAddSheet(true); }} />
        </div>
      )}

      {/* Locked state message */}
      {isClosed && (
        <div style={{
          background: 'var(--color-primary-pale)',
          border: '1.5px solid var(--color-primary-light)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 16px',
          marginBottom: 20,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>✅</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--color-primary)', marginBottom: 4 }}>
              Week closed — your savings are locked in
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-primary)', opacity: 0.8, lineHeight: 1.5 }}>
              New week starts Monday with {formatCurrency(remaining)} carried forward.
            </div>
          </div>
        </div>
      )}

      {/* Sunday end-of-week prompt */}
      {!isClosed && isSunday && spent > 0 && (
        <div style={{
          background: 'var(--color-accent-light)',
          border: '1.5px solid var(--color-accent)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 16px',
          marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#C45E1A' }}>
              🏁 Last day of the week
            </div>
            <div style={{ fontSize: '0.8125rem', color: '#C45E1A', opacity: 0.85, marginTop: 2 }}>
              Ready to close out and lock in your savings?
            </div>
          </div>
          <button
            className="btn"
            style={{ background: 'var(--color-accent)', color: 'white',
              fontSize: '0.8125rem', padding: '8px 16px', flexShrink: 0 }}
            onClick={() => navigate('/close-out')}
          >
            Close out →
          </button>
        </div>
      )}


      {/* Transaction sections */}
      <div className="stack stack-5">
        {/* S2: Planning ahead acknowledgment — planned items exist but nothing spent yet */}
        {plannedTxs.length > 0 && actualTxs.length === 0 && !isClosed && (
          <div style={{
            background: 'var(--color-primary-pale)',
            border: '1.5px solid var(--color-primary-light)',
            borderRadius: 'var(--radius-lg)',
            padding: '14px 16px',
            display: 'flex', gap: 12, alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: 20 }}>📅</span>
            <div>
              <div style={{ fontWeight: 700, color: 'var(--color-primary)', fontSize: '0.9rem', marginBottom: 4 }}>
                Planning before spending. Smart.
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-primary)', opacity: 0.85, lineHeight: 1.5 }}>
                Having a plan is half the battle — now stick to it.
              </div>
            </div>
          </div>
        )}
        {plannedTxs.length > 0 && (
          <TxSection title="Planned this week" emoji="📅"
            transactions={plannedTxs} categories={CATEGORIES}
            color="var(--color-accent)" type="planned"
            onConvert={isClosed ? null : (tx) => setConvertTx(tx)}
            onDelete={isClosed ? null : handleDelete}
            onEdit={isClosed ? null : (tx) => setEditTx(tx)} />
        )}


        <TxSection title="Spent this week" emoji="💸"
          transactions={actualTxs} categories={CATEGORIES}
          color="var(--color-error)" type="actual"
          allTransactions={transactions}
          onDelete={isClosed ? null : handleDelete}
          onEdit={isClosed ? null : (tx) => setEditTx(tx)}
          emptyMessage={(() => {
            const dayOfWeek = new Date().getDay();
            const isLateWeek = dayOfWeek >= 3 && dayOfWeek <= 5; // Wed, Thu, Fri
            const isVeryLate = dayOfWeek === 6 || dayOfWeek === 0; // Sat, Sun
            if (actualTxs.length === 0 && plannedTxs.length === 0 && !isClosed) {
              if (isVeryLate) return (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>⏰</div>
                  <p style={{ color: 'var(--color-text-2)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                    Week ends tonight — catch up on anything you've spent before you close out.
                  </p>
                </div>
              );
              if (isLateWeek) return (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📝</div>
                  <p style={{ color: 'var(--color-text-2)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                    Halfway through the week and nothing logged yet.<br />
                    Quick — jot down what you've spent so far.
                  </p>
                </div>
              );
            }
            if (actualTxs.length > 0 || plannedTxs.length > 0) return null;
            return (
              <div style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-xl)',
                padding: '28px 24px',
                textAlign: 'center',
              }}>
                <div style={{
                  width: 56, height: 56,
                  background: 'var(--color-primary-pale)',
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 26, margin: '0 auto 16px',
                }}>🚀</div>
                <div style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '1.1875rem', fontWeight: 600,
                  color: 'var(--color-text)',
                  marginBottom: 10,
                }}>Week 1 starts now.</div>
                <p style={{
                  color: 'var(--color-text-2)',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  maxWidth: 260,
                  margin: '0 auto',
                }}>
                  Most students have no idea where their money goes. Let's change that!<br />Tap <strong>Plan</strong> to map out your week, or <strong>Spent</strong> to log as you go.
                </p>
              </div>
            );
          })()} />
        {incomeTxs.length > 0 && (
          <TxSection title="Income this week" emoji="🎁"
            transactions={incomeTxs} categories={INCOME_CATEGORIES}
            color="var(--color-primary)" type="income"
            onDelete={isClosed ? null : handleDelete}
            onEdit={isClosed ? null : (tx) => setEditTx(tx)} />
        )}
        {nextWeekTransactions.length > 0 && (
          <NextWeekPreview
            transactions={nextWeekTransactions}
            recurringTemplates={recurringTemplates}
            onDelete={handleDeleteNextWeek} />
        )}
      </div>

      {showReviewPrompt && !reviewPromptDismissed && (
        <ReviewPromptModal
          overdueCount={plannedTxs.filter(t => {
            if (t.spendDate === undefined) return false;
            const todayDay = new Date().getDay();
            const itemPos = t.spendDate === 0 ? 7 : t.spendDate;
            const todayPos = todayDay === 0 ? 7 : todayDay;
            return itemPos < todayPos;
          }).length}
          onReview={() => navigate('/review-spend')}
          onDismiss={() => {
            setShowReviewPrompt(false);
            setReviewPromptDismissed(true);
          }}
        />
      )}

      {showRecurringPrompt && recurringTemplates.length > 0 && (
        <RecurringPromptModal
          templates={recurringTemplates}
          onApply={handleApplyRecurring}
          onDismiss={() => {
            setShowRecurringPrompt(false);
            setRecurringPromptDismissed(true);
            markWeekRecurringApplied(family.id, getCurrentWeekId());
          }}
        />
      )}

      {showAddSheet && (
        <AddSheet defaultType={defaultType}
          onAdd={handleAddTransaction} onClose={() => setShowAddSheet(false)} />
      )}

      {convertTx && (
        <ConvertSheet
          plannedTx={convertTx}
          onConvert={handleConvert}
          onClose={() => setConvertTx(null)}
        />
      )}

      {editTx && (
        <EditSheet
          tx={editTx}
          onSave={handleEdit}
          onDelete={handleDelete}
          onClose={() => setEditTx(null)}
          recurringTemplates={recurringTemplates}
          familyId={family?.id}
        />
      )}

      {backlog && !backlogDismissed && (
        <BacklogModal
          weekCount={backlog.length}
          onConfirm={handleClearBacklog}
          onDismiss={() => setBacklogDismissed(true)}
          loading={backlogClearing}
        />
      )}
    </div>
  );
};

const LegendItem = ({ color, label, value, striped }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    <span style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, display: 'inline-block',
      background: striped
        ? 'repeating-linear-gradient(45deg,#F4A261,#F4A261 3px,#FFDDD2 3px,#FFDDD2 6px)'
        : color }} />
    {label} {value}
  </span>
);

const ActionButton = ({ emoji, label, onClick }) => (
  <button onClick={onClick} style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 5, padding: '12px 8px', borderRadius: 'var(--radius-lg)',
    border: '1.5px solid var(--color-border)',
    background: 'white',
    color: 'var(--color-text)', cursor: 'pointer',
    fontFamily: 'var(--font-body)', transition: 'all 150ms ease',
  }}>
    <span style={{ fontSize: 20 }}>{emoji}</span>
    <span style={{ fontSize: '0.6875rem', fontWeight: 600 }}>{label}</span>
  </button>
);

const TxSection = ({ title, emoji, transactions, categories, color, type, emptyMessage, onConvert, onDelete, onEdit, allTransactions }) => {
  const [expandedId, setExpandedId] = useState(null);

  if (transactions.length === 0 && emptyMessage) {
    return (
      <div>
        <h3 style={{ marginBottom: 12, fontSize: '1rem' }}>{emoji} {title}</h3>
        <div className="card">{emptyMessage}</div>
      </div>
    );
  }
  if (transactions.length === 0) return null;

  const today = todayIndex();

  // Helper: get the display day for a transaction
  // Priority: explicit spendDate → createdAt timestamp day → today
  const getTxDay = (tx) => {
    if (tx.spendDate !== undefined) return tx.spendDate;
    if (tx.createdAt?.toDate) return tx.createdAt.toDate().getDay();
    if (tx.createdAt?.seconds) return new Date(tx.createdAt.seconds * 1000).getDay();
    return today;
  };

  const grouped = transactions.reduce((acc, tx) => {
    const day = getTxDay(tx);
    if (!acc[day]) acc[day] = [];
    acc[day].push(tx);
    return acc;
  }, {});

  // Helper to get timestamp ms from a tx
  const getTxTime = (tx) => {
    if (tx.createdAt?.toDate) return tx.createdAt.toDate().getTime();
    if (tx.createdAt?.seconds) return tx.createdAt.seconds * 1000;
    return 0;
  };

  const sortedDays = Object.keys(grouped)
    .map(Number)
    .sort((a, b) => {
      if (type === 'planned') return a - b; // planned: chronological (upcoming first)
      // actual/income: sort by most recent transaction in each group
      const latestA = Math.max(...grouped[a].map(getTxTime));
      const latestB = Math.max(...grouped[b].map(getTxTime));
      return latestB - latestA; // most recent group first
    });

  const total = transactions.reduce((s, t) => s + t.amount, 0);
  const hasActions = onEdit || onDelete || onConvert;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontSize: '1rem' }}>{emoji} {title}</h3>
        <span style={{ fontWeight: 700, color, fontSize: '0.9375rem' }}>
          {type === 'income' ? '+' : '-'}{formatCurrency(total)}
        </span>
      </div>

      <div className="stack stack-4">
        {sortedDays.map(day => (
          <div key={day}>
            {/* Day header — no subtotal to reduce clutter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                color: type === 'planned' && day === todayIndex()
                  ? '#C45E1A' : 'var(--color-text-3)',
              }}>
                {type === 'planned' && day === todayIndex() ? (
                  <span style={{ fontSize: '0.7rem',
                    background: 'var(--color-accent-light)', color: '#C45E1A',
                    padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
                    Today — don't forget!
                  </span>
                ) : dayLabel(day, type, true)}
              </span>
              <div style={{ flex: 1, height: 1,
                background: type === 'planned' && day === todayIndex()
                  ? 'var(--color-accent)' : 'var(--color-border)' }} />
            </div>

            <div className="stack stack-2">
              {grouped[day].map(tx => {
                const cat = categories.find(c => c.id === tx.category) || categories[categories.length - 1];
                const isExpanded = expandedId === tx.id;
                // Build subtitle: category + type label + day
                const txDay = tx.spendDate !== undefined ? tx.spendDate
                  : tx.createdAt?.toDate ? tx.createdAt.toDate().getDay()
                  : tx.createdAt?.seconds ? new Date(tx.createdAt.seconds * 1000).getDay()
                  : todayIndex();
                const dayStr = dayLabel(txDay);
                const typeLabel = type === 'planned' ? '· planned' : type === 'income' ? '· income' : null;
                // If this was converted from a planned item, find the original planned amount
                const originalPlanned = tx.convertedFromId
                  ? (allTransactions || transactions).find(t => t.id === tx.convertedFromId)
                  : null;
                return (
                  <div key={tx.id}>
                    <div className="card"
                      onClick={() => hasActions && setExpandedId(isExpanded ? null : tx.id)}
                      style={{ padding: '12px 14px', display: 'flex', alignItems: 'center',
                        gap: 12, borderLeft: `3px solid ${color}`,
                        cursor: hasActions ? 'pointer' : 'default',
                        borderBottomLeftRadius: isExpanded ? 0 : undefined,
                        borderBottomRightRadius: isExpanded ? 0 : undefined,
                        borderBottom: isExpanded ? 'none' : undefined,
                      }}>
                      <div style={{ width: 38, height: 38, borderRadius: 10,
                        background: 'var(--color-surface-2)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                        {cat.emoji}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tx.note || cat.label}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 1,
                          display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span>{cat.label}</span>
                          {typeLabel && (
                            <span style={{ color: type === 'planned' ? 'var(--color-accent)' : 'var(--color-primary)',
                              fontWeight: 600 }}>{typeLabel}</span>
                          )}
                          {/* Show planned → actual variance if converted */}
                          {originalPlanned && originalPlanned.amount !== tx.amount && (
                            <span style={{ color: 'var(--color-text-3)' }}>
                              · planned {formatCurrency(originalPlanned.amount)}
                              {tx.amount > originalPlanned.amount
                                ? <span style={{ color: 'var(--color-error)' }}> ↑</span>
                                : <span style={{ color: 'var(--color-primary)' }}> ↓</span>
                              }
                            </span>
                          )}
                          <span>· {dayStr}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, color }}>
                          {type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                        </div>
                        {hasActions && (
                          <span style={{ color: 'var(--color-text-3)', fontSize: '0.7rem' }}>
                            {isExpanded ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Expanded action bar */}
                    {isExpanded && (
                      <div style={{ display: 'flex',
                        border: '1px solid var(--color-border)', borderTop: 'none',
                        borderRadius: '0 0 var(--radius-lg) var(--radius-lg)', overflow: 'hidden' }}>
                        {type === 'planned' && onConvert && (
                          <ActionBarBtn label="✓ Spent it"
                            color="var(--color-primary)" bg="var(--color-primary-pale)"
                            onClick={() => { setExpandedId(null); onConvert(tx); }} />
                        )}
                        {onEdit && (
                          <ActionBarBtn label="✏️ Edit"
                            color="var(--color-text-2)" bg="var(--color-surface-2)"
                            onClick={() => { setExpandedId(null); onEdit(tx); }} />
                        )}
                        {onDelete && (
                          <ActionBarBtn label="🗑 Delete"
                            color="var(--color-error)" bg="var(--color-error-light)"
                            onClick={() => { setExpandedId(null); onDelete(tx); }} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ActionBarBtn = ({ label, color, bg, onClick }) => (
  <button onClick={onClick} style={{
    flex: 1, padding: '10px 8px', border: 'none',
    borderRight: '1px solid var(--color-border)',
    background: bg, color, fontWeight: 700, fontSize: '0.8125rem',
    cursor: 'pointer', fontFamily: 'var(--font-body)', transition: 'opacity 150ms ease',
  }}>
    {label}
  </button>
);

// Sheet for converting a planned transaction to actual
const ConvertSheet = ({ plannedTx, onConvert, onClose }) => {
  const cat = CATEGORIES.find(c => c.id === plannedTx.category) || CATEGORIES[CATEGORIES.length - 1];
  const [amount, setAmount] = useState(plannedTx.amount.toString());
  const [spendDate, setSpendDate] = useState(
    plannedTx.spendDate !== undefined ? plannedTx.spendDate : todayIndex()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const val = parseFloat(amount);
    if (!val || val <= 0) { setError('Enter a valid amount'); return; }
    setLoading(true);
    if (document.activeElement) document.activeElement.blur();
    try {
      await onConvert(val, spendDate);
    } catch (err) {
      setError('Something went wrong. Try again.');
      setLoading(false);
    }
  };

  // Days going backward from today (you're confirming something you already spent)
  const today = todayIndex();
  const dayOptions = [0,1,2,3,4,5,6].map(offset => {
    const idx = (today - offset + 7) % 7;
    return { index: idx, label: offset === 0 ? 'Today' : offset === 1 ? 'Yesterday' : DAYS[idx] };
  });

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={e => e.stopPropagation()}>
        <div style={handleStyle} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12,
            background: 'var(--color-surface-2)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
            {cat.emoji}
          </div>
          <div>
            <div style={{ fontWeight: 700 }}>{plannedTx.note || cat.label}</div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-3)' }}>
              Planned {formatCurrency(plannedTx.amount)}
            </div>
          </div>
        </div>

        <h3 style={{ marginBottom: 6 }}>How much did you spend?</h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-2)', marginBottom: 20 }}>
          Adjust the amount if it differed from your plan, then confirm.
        </p>

        <form onSubmit={handleSubmit} className="stack stack-4">
          <div className="form-group">
            <label className="form-label">Actual amount spent</label>
            <div className="input-with-prefix">
              <span className="input-prefix">$</span>
              <input className="form-input" type="number" min="0.01" step="0.01"
                value={amount}
                onChange={e => { setAmount(e.target.value); setError(''); }}
                style={{ fontSize: '1.5rem', fontFamily: 'var(--font-display)', fontWeight: 700 }} />
            </div>
            {parseFloat(amount) !== plannedTx.amount && parseFloat(amount) > 0 && (
              <div className="form-hint">
                {parseFloat(amount) > plannedTx.amount
                  ? `${formatCurrency(parseFloat(amount) - plannedTx.amount)} over plan`
                  : `${formatCurrency(plannedTx.amount - parseFloat(amount))} under plan`}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">When did you spend it?</label>
            <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
              <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
                {dayOptions.map(({ index, label }) => (
                  <button key={index} type="button" onClick={() => setSpendDate(index)}
                    style={{
                      padding: '8px 14px', borderRadius: 'var(--radius-full)',
                      border: '1.5px solid',
                      borderColor: spendDate === index ? 'var(--color-primary)' : 'var(--color-border)',
                      background: spendDate === index ? 'var(--color-primary-pale)' : 'white',
                      color: spendDate === index ? 'var(--color-primary)' : 'var(--color-text-2)',
                      fontWeight: spendDate === index ? 700 : 400,
                      fontSize: '0.8125rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                      whiteSpace: 'nowrap', transition: 'all 150ms ease',
                    }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {error && <div className="alert alert--error">{error}</div>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn--secondary"
              onClick={onClose} style={{ flex: 1 }}>Cancel</button>
            <button type="submit" className="btn btn--primary"
              disabled={loading} style={{ flex: 2 }}>
              {loading
                ? <span className="spinner spinner--white" style={{ width: 18, height: 18 }} />
                : 'Confirm Spend ✓'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit sheet — edit amount, note, day, or category of any transaction
const EditSheet = ({ tx, onSave, onDelete, onClose, recurringTemplates = [], familyId }) => {
  const isIncome = tx.type === 'income';
  const isPlanned = tx.type === 'planned';
  const categories = isIncome ? INCOME_CATEGORIES : CATEGORIES;
  const cat = categories.find(c => c.id === tx.category) || categories[categories.length - 1];

  // Check if this transaction matches an existing recurring template
  const matchingTemplate = isPlanned ? recurringTemplates.find(t =>
    t.category === tx.category &&
    t.spendDay === (tx.spendDate !== undefined ? tx.spendDate : todayIndex()) &&
    Math.abs(t.amount - tx.amount) < 0.01
  ) : null;

  const [amount, setAmount] = useState(tx.amount.toString());
  const [category, setCategory] = useState(tx.category);
  const [note, setNote] = useState(tx.note || '');
  const [spendDate, setSpendDate] = useState(tx.spendDate !== undefined ? tx.spendDate : todayIndex());
  const [repeatWeekly, setRepeatWeekly] = useState(!!matchingTemplate);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  const today = todayIndex();

  // For planned: all days; for actual/income: going backward
  const dayOptions = isPlanned
    ? DAYS.map((d, i) => ({ index: i, label: i === today ? 'Today' : d }))
    : [0,1,2,3,4,5,6].map(offset => {
        const idx = (today - offset + 7) % 7;
        return { index: idx, label: offset === 0 ? 'Today' : offset === 1 ? 'Yesterday' : DAYS[idx] };
      });

  const handleSave = async (e) => {
    e.preventDefault();
    const val = parseFloat(amount);
    if (!val || val <= 0) { setError('Enter a valid amount'); return; }
    setLoading(true);
    if (document.activeElement) document.activeElement.blur();
    try {
      await onSave(tx, { amount: val, category, note, spendDate });
      // Handle recurring template changes
      if (isPlanned && familyId) {
        if (repeatWeekly && !matchingTemplate) {
          // Newly toggled on — save template and mark this week as applied
          // so the RecurringPromptModal doesn't fire and create a duplicate
          await saveRecurringTemplate(familyId, { amount: val, category, note, spendDay: spendDate });
          await markWeekRecurringApplied(familyId, getCurrentWeekId());
        } else if (!repeatWeekly && matchingTemplate) {
          // Toggled off — remove template
          await deleteRecurringTemplate(familyId, matchingTemplate.id);
        } else if (repeatWeekly && matchingTemplate) {
          // Still on but fields may have changed — remove old, add new
          await deleteRecurringTemplate(familyId, matchingTemplate.id);
          await saveRecurringTemplate(familyId, { amount: val, category, note, spendDay: spendDate });
        }
      }
    } catch (err) {
      setError('Failed to save. Try again.');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onDelete(tx);
      onClose();
    } catch (err) {
      setError('Failed to delete. Try again.');
      setLoading(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={e => e.stopPropagation()}>
        <div style={handleStyle} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10,
              background: 'var(--color-surface-2)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
              {cat.emoji}
            </div>
            <div>
              <h3 style={{ fontSize: '1rem' }}>Edit entry</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
                {tx.type === 'planned' ? 'Planned spend' : tx.type === 'income' ? 'Income' : 'Actual spend'}
              </div>
            </div>
          </div>
          {/* Delete trigger */}
          {!confirmDelete && (
            <button onClick={() => setConfirmDelete(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-error)', fontSize: '0.8125rem', fontWeight: 600,
                fontFamily: 'var(--font-body)' }}>
              🗑 Delete
            </button>
          )}
        </div>

        {/* Confirm delete inline */}
        {confirmDelete && (
          <div className="alert alert--error" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>Delete this entry?</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn--secondary" style={{ flex: 1, fontSize: '0.875rem' }}
                onClick={() => setConfirmDelete(false)}>Keep it</button>
              <button className="btn btn--danger" style={{ flex: 1, fontSize: '0.875rem' }}
                onClick={handleDelete} disabled={loading}>
                {loading ? <span className="spinner spinner--white" style={{ width: 16, height: 16 }} /> : 'Yes, delete'}
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSave} className="stack stack-4">
          {/* Amount */}
          <div className="form-group">
            <label className="form-label">Amount</label>
            <div className="input-with-prefix">
              <span className="input-prefix">$</span>
              <input className="form-input" type="number" min="0.01" step="0.01"
                value={amount} onChange={e => { setAmount(e.target.value); setError(''); }}
                style={{ fontSize: '1.25rem', fontFamily: 'var(--font-display)', fontWeight: 600 }} />
            </div>
          </div>

          {/* Day picker */}
          <div className="form-group">
            <label className="form-label">{isPlanned ? 'Which day?' : 'When?'}</label>
            <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
              <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
                {dayOptions.map(({ index, label }) => (
                  <button key={index} type="button" onClick={() => setSpendDate(index)}
                    style={{
                      padding: '8px 14px', borderRadius: 'var(--radius-full)', border: '1.5px solid',
                      borderColor: spendDate === index ? 'var(--color-primary)' : 'var(--color-border)',
                      background: spendDate === index ? 'var(--color-primary-pale)' : 'white',
                      color: spendDate === index ? 'var(--color-primary)' : 'var(--color-text-2)',
                      fontWeight: spendDate === index ? 700 : 400,
                      fontSize: '0.8125rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                      whiteSpace: 'nowrap', transition: 'all 150ms ease',
                    }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Category */}
          <div className="form-group">
            <label className="form-label">Category</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {categories.map(c => (
                <button key={c.id} type="button" onClick={() => setCategory(c.id)}
                  style={{
                    padding: '10px 4px', borderRadius: 'var(--radius-md)', border: '2px solid',
                    borderColor: category === c.id ? 'var(--color-primary)' : 'var(--color-border)',
                    background: category === c.id ? 'var(--color-primary-pale)' : 'white',
                    cursor: 'pointer', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 4, transition: 'all 150ms ease',
                    fontFamily: 'var(--font-body)',
                  }}>
                  <span style={{ fontSize: 20 }}>{c.emoji}</span>
                  <span style={{ fontSize: '0.65rem', fontWeight: 600,
                    color: category === c.id ? 'var(--color-primary)' : 'var(--color-text-3)' }}>
                    {c.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="form-group">
            <label className="form-label">
              Note <span style={{ fontWeight: 400, color: 'var(--color-text-3)' }}>(optional)</span>
            </label>
            <input className="form-input" type="text" placeholder="Add a note..."
              value={note} onChange={e => setNote(e.target.value)} maxLength={60} />
          </div>

          {/* Repeat weekly toggle — planned items only */}
          {isPlanned && (
            <div className="toggle-wrapper" style={{ padding: '4px 0' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 2 }}>
                  Repeat weekly
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
                  {matchingTemplate && !repeatWeekly
                    ? 'Turn off to remove from recurring items'
                    : 'Add this to your plan every week automatically'}
                </div>
              </div>
              <label className="toggle">
                <input type="checkbox" checked={repeatWeekly}
                  onChange={e => setRepeatWeekly(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
          )}

          {error && <div className="alert alert--error">{error}</div>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn--secondary"
              onClick={onClose} style={{ flex: 1 }}>Cancel</button>
            <button type="submit" className="btn btn--primary"
              disabled={loading} style={{ flex: 2 }}>
              {loading
                ? <span className="spinner spinner--white" style={{ width: 18, height: 18 }} />
                : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NextWeekPreview = ({ transactions, onDelete, recurringTemplates = [] }) => {
  const [open, setOpen] = useState(false);
  const total = transactions.reduce((s, t) => s + t.amount, 0);

  return (
    <>
      {/* "Next week" divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-3)',
          letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
          next week
        </span>
        <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden',
        background: 'var(--color-surface-2)' }}>
      {/* Header — always visible */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px', cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>📋</span>
          <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>Next week</span>
          <span style={{
            fontSize: '0.7rem', fontWeight: 700,
            background: 'var(--color-primary-pale)', color: 'var(--color-primary)',
            padding: '2px 8px', borderRadius: 'var(--radius-full)',
          }}>
            {transactions.length} planned
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--color-text-2)' }}>
            -{formatCurrency(total)}
          </span>
          <span style={{
            fontSize: '0.7rem', color: 'var(--color-text-3)',
            transition: 'transform 200ms',
            display: 'inline-block',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>▼</span>
        </div>
      </div>

      {/* Body — collapsed by default */}
      {open && (
        <div style={{ borderTop: '1px solid var(--color-border)', padding: '10px 16px 14px' }}>
          <div className="stack stack-2">
            {transactions.map(tx => {
              const cat = CATEGORIES.find(c => c.id === tx.category) || CATEGORIES[CATEGORIES.length - 1];
              const dayName = tx.spendDate !== undefined ? DAYS[tx.spendDate] : 'Next week';
              return (
                <div key={tx.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 0',
                  borderBottom: '1px solid var(--color-border)',
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: 'var(--color-surface-2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 15, flexShrink: 0,
                  }}>
                    {cat.emoji}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.note || cat.label}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)',
                      marginTop: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {dayName}
                      {recurringTemplates.some(t =>
                        t.category === tx.category &&
                        t.spendDay === tx.spendDate &&
                        Math.abs(t.amount - tx.amount) < 0.01
                      ) && (
                        <span style={{ fontSize: 10, opacity: 0.7 }} title="Recurring">🔁</span>
                      )}
                    </div>
                  </div>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600,
                    color: 'var(--color-accent)', flexShrink: 0 }}>
                    -{formatCurrency(tx.amount)}
                  </span>
                  <button
                    onClick={() => onDelete(tx)}
                    style={{
                      background: 'none', border: '1px solid var(--color-border)',
                      borderRadius: 6, width: 26, height: 26,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: 'var(--color-text-3)',
                      fontSize: '0.7rem', flexShrink: 0, fontFamily: 'var(--font-body)',
                    }}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
          <p style={{
            fontSize: '0.75rem', color: 'var(--color-text-3)',
            marginTop: 12, lineHeight: 1.5,
          }}>
            These will appear in your dashboard when next week starts. You can remove them but not convert them yet.
          </p>
        </div>
      )}
    </div>
    </>
  );
};

const AddSheet = ({ defaultType, onAdd, onClose }) => {
  const [type, setType] = useState(defaultType);
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState(defaultType === 'income' ? 'gift' : 'food');
  const [note, setNote] = useState('');
  // Planned defaults to tomorrow unless today is Sunday (last day of week) — then default to today
  const [spendDate, setSpendDate] = useState(
    defaultType === 'planned'
      ? (todayIndex() === 0 ? 0 : (todayIndex() + 1) % 7)
      : todayIndex()
  );
  const [weekOffset, setWeekOffset] = useState(0); // 0 = this week, 1 = next week
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categories = type === 'income' ? INCOME_CATEGORIES : CATEGORIES;
  const typeConfig = {
    actual: { label: 'Log Spending', emoji: '💸', color: 'var(--color-error)' },
    planned: { label: 'Plan Spending', emoji: '📅', color: 'var(--color-accent)' },
    income: { label: 'Add Income', emoji: '🎁', color: 'var(--color-primary)' },
  };

  const handleTypeChange = (t) => {
    setType(t);
    setCategory(t === 'income' ? 'gift' : 'food');
    // For planned: default to tomorrow unless today is Sunday (last day of week) — then default to today
    // For actual/income: default to today
    const today = todayIndex();
    // On Sunday (day 0), planned defaults to today not tomorrow (tomorrow is next week)
    const plannedDefault = t === 'planned'
      ? (today === 0 ? 0 : (today + 1) % 7)
      : today;
    setSpendDate(plannedDefault);
    setWeekOffset(0);
    if (t !== 'planned') setRepeatWeekly(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const val = parseFloat(amount);
    if (!val || val <= 0) { setError('Enter a valid amount'); return; }
    setLoading(true);
    // Blur active input before closing to prevent iOS "Undo Typing" alert
    if (document.activeElement) document.activeElement.blur();
    try {
      await onAdd({ amount: val, category, note, type, spendDate, weekOffset, repeatWeekly });
    } catch (err) {
      setError('Failed to save. Try again.');
      setLoading(false);
    }
  };

  // Build ordered day list:
  // - next week planned: Mon-Sun of next week (no Today/Tomorrow labels)
  // - this week planned: today through end of week (Sunday), capped
  // - actual/income: today back to start of week (Monday), capped
  const today = todayIndex();
  // Days remaining this week including today: today=Mon(1) → 6 days left, today=Sun(0) → 1 day (just Today)
  const daysLeftThisWeek = today === 0 ? 1 : 8 - today; // Sun=1, Mon=7, Tue=6...Sat=2
  // Days since Monday including today: Mon=1, Tue=2 ... Sun=7
  const daysSinceMonday = today === 0 ? 7 : today;
  const dayOptions = type === 'planned' && weekOffset === 1
    ? [1,2,3,4,5,6,0].map(dayIdx => ({ // Mon=1 through Sun=0
        index: dayIdx,
        label: DAYS[dayIdx],
      }))
    : type === 'planned'
      ? Array.from({ length: daysLeftThisWeek }, (_, offset) => {
          const idx = (today + offset) % 7;
          return { index: idx, label: offset === 0 ? 'Today' : offset === 1 ? 'Tomorrow' : DAYS[idx] };
        })
      : Array.from({ length: daysSinceMonday }, (_, offset) => {
          const idx = (today - offset + 7) % 7;
          return { index: idx, label: offset === 0 ? 'Today' : offset === 1 ? 'Yesterday' : DAYS[idx] };
        });

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={e => e.stopPropagation()}>
        <div style={handleStyle} />

        {/* Type toggle */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 6, marginBottom: 20, background: 'var(--color-surface-2)',
          padding: 4, borderRadius: 'var(--radius-md)' }}>
          {Object.entries(typeConfig).map(([t, cfg]) => (
            <button key={t} type="button" onClick={() => handleTypeChange(t)}
              style={{
                padding: '10px 4px', borderRadius: 8, border: 'none',
                background: type === t ? 'white' : 'transparent',
                color: type === t ? cfg.color : 'var(--color-text-3)',
                fontWeight: type === t ? 700 : 400,
                fontSize: '0.8125rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                boxShadow: type === t ? 'var(--shadow-sm)' : 'none',
                transition: 'all 150ms ease',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              }}>
              <span style={{ fontSize: 16 }}>{cfg.emoji}</span>
              <span>{t === 'actual' ? 'Spent' : t === 'planned' ? 'Plan' : 'Income'}</span>
            </button>
          ))}
        </div>

        {/* Week toggle — only shown for planned */}
        {type === 'planned' && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 16,
            background: 'var(--color-surface-2)', padding: 4, borderRadius: 'var(--radius-md)' }}>
            {[{ label: 'This week', value: 0 }, { label: 'Next week', value: 1 }].map(({ label, value }) => (
              <button key={value} type="button" onClick={() => {
                setWeekOffset(value);
                // Default to Monday (1) for next week, tomorrow for this week
                setSpendDate(value === 1 ? 1 : (todayIndex() === 0 ? 0 : (todayIndex() + 1) % 7));
              }}
                style={{
                  flex: 1, padding: '9px 4px', borderRadius: 8, border: 'none',
                  background: weekOffset === value ? 'white' : 'transparent',
                  color: weekOffset === value ? 'var(--color-accent)' : 'var(--color-text-3)',
                  fontWeight: weekOffset === value ? 700 : 400,
                  fontSize: '0.8125rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                  boxShadow: weekOffset === value ? 'var(--shadow-sm)' : 'none',
                  transition: 'all 150ms ease',
                }}>
                {label}
              </button>
            ))}
          </div>
        )}

        <h3 style={{ marginBottom: 16, color: typeConfig[type].color }}>
          {typeConfig[type].label}
        </h3>

        <form onSubmit={handleSubmit} className="stack stack-3">
          {/* Amount */}
          <div className="form-group">
            <label className="form-label">Amount</label>
            <div className="input-with-prefix">
              <span className="input-prefix">$</span>
              <input className="form-input" type="number" min="0.01" step="0.01"
                placeholder="0.00" value={amount}
                onChange={e => { setAmount(e.target.value); setError(''); }}
                style={{ fontSize: '1.25rem', fontFamily: 'var(--font-display)', fontWeight: 600 }} />
            </div>
          </div>

          {/* Day picker */}
          <div className="form-group">
            <label className="form-label">
              {type === 'planned' ? 'Which day?' : 'When?'}
            </label>
            <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
              <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
                {dayOptions.map(({ index, label }) => (
                  <button key={index} type="button" onClick={() => setSpendDate(index)}
                    style={{
                      padding: '8px 14px', borderRadius: 'var(--radius-full)',
                      border: '1.5px solid',
                      borderColor: spendDate === index ? typeConfig[type].color : 'var(--color-border)',
                      background: spendDate === index ? 'var(--color-primary-pale)' : 'white',
                      color: spendDate === index ? 'var(--color-primary)' : 'var(--color-text-2)',
                      fontWeight: spendDate === index ? 700 : 400,
                      fontSize: '0.8125rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                      whiteSpace: 'nowrap', transition: 'all 150ms ease',
                    }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Category grid */}
          <div className="form-group">
            <label className="form-label">Category</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {categories.map(cat => (
                <button key={cat.id} type="button" onClick={() => setCategory(cat.id)}
                  style={{
                    padding: '10px 4px', borderRadius: 'var(--radius-md)', border: '2px solid',
                    borderColor: category === cat.id ? 'var(--color-primary)' : 'var(--color-border)',
                    background: category === cat.id ? 'var(--color-primary-pale)' : 'white',
                    cursor: 'pointer', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 4, transition: 'all 150ms ease',
                    fontFamily: 'var(--font-body)',
                  }}>
                  <span style={{ fontSize: 20 }}>{cat.emoji}</span>
                  <span style={{ fontSize: '0.65rem', fontWeight: 600,
                    color: category === cat.id ? 'var(--color-primary)' : 'var(--color-text-3)' }}>
                    {cat.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="form-group">
            <label className="form-label">
              Note <span style={{ fontWeight: 400, color: 'var(--color-text-3)' }}>(optional)</span>
            </label>
            <input className="form-input" type="text"
              placeholder={
                type === 'income' ? 'e.g. Birthday money from Grandma' :
                type === 'planned' ? 'e.g. Groceries on Thursday' :
                'e.g. Pizza with friends'
              }
              value={note} onChange={e => setNote(e.target.value)} maxLength={60}
              onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)} />
          </div>

          {/* Repeat weekly toggle — planned items only */}
          {type === 'planned' && (
            <div className="toggle-wrapper" style={{ padding: '4px 0' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 2 }}>
                  Repeat weekly
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
                  Add this to your plan every week automatically
                </div>
              </div>
              <label className="toggle">
                <input type="checkbox" checked={repeatWeekly}
                  onChange={e => setRepeatWeekly(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
          )}

          {error && <div className="alert alert--error">{error}</div>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn--secondary"
              onClick={onClose} style={{ flex: 1 }}>Cancel</button>
            <button type="submit" className="btn btn--primary"
              disabled={loading} style={{ flex: 2 }}>
              {loading
                ? <span className="spinner spinner--white" style={{ width: 18, height: 18 }} />
                : typeConfig[type].label}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ─── Recurring Prompt Modal ────────────────────────────────────────────────
// Shown at the start of each new week when recurring templates exist and
// haven't been applied yet. Student can select which items to add, or dismiss.

const RecurringPromptModal = ({ templates, onApply, onDismiss }) => {
  const [selected, setSelected] = useState(templates.map(t => t.id));
  const [loading, setLoading] = useState(false);

  const toggle = (id) => setSelected(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div style={{ ...overlayStyle, alignItems: 'center', padding: '0 20px' }}
      onClick={onDismiss}>
      <div style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-xl)',
        padding: '24px 20px 20px',
        width: '100%',
        maxWidth: 'var(--max-width)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        animation: 'slideUp 250ms ease',
      }} onClick={e => e.stopPropagation()}>

        <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 12 }}>📅</div>
        <h3 style={{ textAlign: 'center', marginBottom: 8 }}>New week, same plan?</h3>
        <p style={{ textAlign: 'center', fontSize: '0.875rem',
          color: 'var(--color-text-2)', lineHeight: 1.6, marginBottom: 20 }}>
          You have {templates.length} recurring {templates.length === 1 ? 'item' : 'items'}.
          Add {templates.length === 1 ? 'it' : 'them'} to this week's plan?
        </p>

        <div className="stack stack-3" style={{ marginBottom: 20 }}>
          {templates.map(t => (
            <div key={t.id}
              onClick={() => toggle(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px', borderRadius: 'var(--radius-lg)',
                border: '1.5px solid',
                borderColor: selected.includes(t.id) ? 'var(--color-primary)' : 'var(--color-border)',
                background: selected.includes(t.id) ? 'var(--color-primary-pale)' : 'var(--color-surface)',
                cursor: 'pointer', transition: 'all 150ms ease',
              }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                border: '2px solid',
                borderColor: selected.includes(t.id) ? 'var(--color-primary)' : 'var(--color-border)',
                background: selected.includes(t.id) ? 'var(--color-primary)' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {selected.includes(t.id) && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M1.5 5l2.5 2.5L8.5 2" stroke="white" strokeWidth="1.5"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9375rem',
                  color: 'var(--color-text)', marginBottom: 2 }}>
                  {t.note || CATEGORIES.find(c => c.id === t.category)?.label || t.category}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
                  {DAYS[t.spendDay]} · {CATEGORIES.find(c => c.id === t.category)?.emoji} {CATEGORIES.find(c => c.id === t.category)?.label}
                </div>
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700,
                fontSize: '1rem', color: selected.includes(t.id) ? 'var(--color-primary)' : 'var(--color-text-2)',
                flexShrink: 0 }}>
                ${t.amount.toFixed(2)}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn--secondary" onClick={onDismiss} style={{ flex: 1 }}>
            Skip this week
          </button>
          <button
            className="btn btn--primary"
            style={{ flex: 2 }}
            disabled={selected.length === 0 || loading}
            onClick={async () => {
              setLoading(true);
              await onApply(selected);
            }}>
            {loading
              ? <span className="spinner spinner--white" style={{ width: 18, height: 18 }} />
              : `Add to plan ✓ (${selected.length})`}
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── Recurring Manager ────────────────────────────────────────────────────
// Collapsible section showing existing recurring templates with delete option.
// Sits below the planned items section on the dashboard.

const RecurringManager = ({ templates, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div style={{ marginBottom: 16 }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
          color: 'var(--color-text-3)', fontSize: '0.8125rem',
          fontFamily: 'var(--font-body)', fontWeight: 500,
          padding: '4px 0',
        }}>
        <span style={{ fontSize: 14 }}>🔁</span>
        {templates.length} recurring {templates.length === 1 ? 'item' : 'items'}
        <span style={{
          display: 'inline-block',
          transform: expanded ? 'rotate(180deg)' : 'none',
          transition: 'transform 200ms', fontSize: 10,
        }}>▼</span>
      </button>

      {expanded && (
        <div className="stack stack-2 animate-in" style={{ marginTop: 8 }}>
          {templates.map(t => (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 14px', borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>
                {CATEGORIES.find(c => c.id === t.category)?.emoji || '📦'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem',
                  color: 'var(--color-text)', whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {t.note || CATEGORIES.find(c => c.id === t.category)?.label || t.category}
                </div>
                <div style={{ fontSize: '0.6875rem', color: 'var(--color-text-3)', marginTop: 1 }}>
                  Every {DAYS[t.spendDay]} · ${t.amount.toFixed(2)}
                </div>
              </div>
              <button
                onClick={async () => {
                  setDeletingId(t.id);
                  await onDelete(t.id);
                  setDeletingId(null);
                }}
                disabled={deletingId === t.id}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--color-error)', fontSize: '0.75rem',
                  fontWeight: 600, fontFamily: 'var(--font-body)',
                  flexShrink: 0, padding: '4px 8px',
                }}>
                {deletingId === t.id
                  ? <span className="spinner" style={{ width: 14, height: 14 }} />
                  : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ─── Review Prompt Modal ──────────────────────────────────────────────────────
// Blocking modal that appears when overdue planned items exist.
// Dismissible for this session — reappears on next load until resolved.

const ReviewPromptModal = ({ overdueCount, onReview, onDismiss }) => (
  <div style={{ ...overlayStyle, alignItems: 'center', padding: '0 20px' }}
    onClick={onDismiss}>
    <div style={{
      background: 'var(--color-surface)',
      borderRadius: 'var(--radius-xl)',
      padding: '28px 24px 24px',
      width: '100%',
      maxWidth: 'var(--max-width)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      animation: 'slideUp 250ms ease',
    }} onClick={e => e.stopPropagation()}>

      <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>📋</div>
      <h3 style={{ textAlign: 'center', marginBottom: 8 }}>
        Time to update your plan
      </h3>
      <p style={{
        textAlign: 'center', fontSize: '0.875rem',
        color: 'var(--color-text-2)', lineHeight: 1.6, marginBottom: 24,
      }}>
        You have <strong>{overdueCount} planned {overdueCount === 1 ? 'item' : 'items'}</strong> from
        earlier this week that {overdueCount === 1 ? "hasn't" : "haven't"} been logged yet.{' '}
        {overdueCount === 1 ? 'Did it happen?' : 'Did they happen?'}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button
          className="btn btn--primary btn--full"
          onClick={onReview}>
          Review now →
        </button>
        <button
          className="btn btn--secondary btn--full"
          onClick={onDismiss}>
          I'll do it later
        </button>
      </div>
    </div>
  </div>
);

// ─── Backlog Modal ─────────────────────────────────────────────────────────
// Shown when student opens the app with 2+ unclosed prior weeks.
// Gives them a clean "start fresh" escape hatch — marks those weeks closed
// with $0 saved and chains carryover correctly into the current week.

const BacklogModal = ({ weekCount, onConfirm, onDismiss, loading }) => (
  <div style={{ ...overlayStyle, alignItems: 'center', padding: '0 20px' }}
    onClick={onDismiss}>
    <div style={{
      background: 'var(--color-surface)',
      borderRadius: 'var(--radius-xl, 20px)',
      padding: '28px 24px 24px',
      width: '100%',
      maxWidth: 'var(--max-width)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      animation: 'slideUp 250ms ease',
    }}
      onClick={e => e.stopPropagation()}>

      {/* Icon */}
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: '#FFF3E0', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: 26, margin: '0 auto 18px',
      }}>
        📅
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '1.125rem', marginBottom: 10 }}>
        You missed a few weeks
      </h2>
      <p style={{
        textAlign: 'center', fontSize: '0.875rem',
        color: 'var(--color-text-2)', lineHeight: 1.6, marginBottom: 24,
      }}>
        Looks like <strong>{weekCount} weeks</strong> haven't been closed out.
        No worries — tap <em>Start fresh</em> to clear them with $0 saved and
        pick up right where you are now.{' '}
        Your parent will be notified.
      </p>

      {/* What happens breakdown */}
      <div style={{
        background: 'var(--color-surface-2)',
        borderRadius: 'var(--radius-lg)',
        padding: '14px 16px',
        marginBottom: 20,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {[
          ['📭', `${weekCount} old weeks marked closed`],
          ['💰', 'Savings locked at $0 for those weeks'],
          ['➡️', 'Any leftover balance carried into today'],
          ['🔔', 'Your parent will see a summary'],
        ].map(([emoji, text]) => (
          <div key={text} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 15, flexShrink: 0 }}>{emoji}</span>
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)' }}>{text}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          className="btn btn--secondary"
          onClick={onDismiss}
          style={{ flex: 1, fontSize: '0.875rem' }}
          disabled={loading}
        >
          Remind me later
        </button>
        <button
          className="btn btn--primary"
          onClick={onConfirm}
          style={{ flex: 2, fontSize: '0.875rem' }}
          disabled={loading}
        >
          {loading
            ? <span className="spinner spinner--white" style={{ width: 18, height: 18 }} />
            : 'Start fresh ✓'}
        </button>
      </div>
    </div>
  </div>
);

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  zIndex: 200, display: 'flex', alignItems: 'flex-end',
  animation: 'fadeIn 200ms ease',
};
const sheetStyle = {
  background: 'var(--color-surface)', borderRadius: '20px 20px 0 0',
  padding: '12px 24px 40px', width: '100%', maxWidth: 'var(--max-width)',
  margin: '0 auto', boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
  animation: 'slideUp 250ms ease',
  maxHeight: '92vh',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
};
const handleStyle = {
  width: 36, height: 4, borderRadius: 2, background: 'var(--color-border)',
  margin: '0 auto 20px', display: 'block',
};

export default StudentDashboard;
